<template>
    <div class="ChannelCard well text-sm p-10" style="border: 0;">
        <div class="flex">
            <h1 class="m-r-md">
                <span style="color: #ff00a7;font-size: 50px;" :class="platform.icon"></span>
            </h1>
            <div class="flex-1">
                <div class="ChannelCard__status pull-right">
                    <span v-if="!channel">
                        Ikke forbundet
                    </span>
                    <span v-else-if="channelConnected">
                        <status-indicator type="success" :beacon="true"></status-indicator>
                        Forbundet
                    </span>
                    <span v-else-if="channelFailed">
                        <status-indicator type="danger"></status-indicator> Fejl
                    </span>
                    <span v-else>
                        <status-indicator type="warning"></status-indicator> Forbinder...
                    </span>
                </div>

                <h2 class="m-b-lg">
                    <slot name="name">
                        <p style="font-weight: bold; font-size: 18px; margin-top: 2px;">
                            {{ platform.name }}
                        </p>
                    </slot>
                </h2>

                <div v-if="channelFailed" class="alert alert-danger well-sm">
                    <template v-if="isPersonalAccount">
                        Du har registreret en personlig Instagram-konto. For at forbinde til 
                        Betterhype skal du have en Business- eller Creator-konto.
                        <a
                            href="https://help.instagram.com/502981923235522"
                            target="_blank"
                            rel="noopener"
                        >
                            Sådan skifter du.
                        </a>
                    </template>
                    <slot v-else name="error-message">
                        Der opstod en fejl i forsøget på at forbinde til din konto. Prøv venligst
                        igen, og kontroller du forbinder til den rigtige konto.
                    </slot>
                </div>

                <p class="m-b-lg">
                    <slot name="description"></slot>
                </p>

                <div v-if="channel" class="row m-t-lg m-b-xl">
                    <div class="col-sm-4 m-t-lg hidden">
                        <h5>Betaling pr. opgave</h5>
                        <amount :value="channel.influencer_job_profit" class="lead"></amount>
                        <div v-if="channelConnected" class="small">
                            Baseret på {{ channel.reach }} {{ platform.reach_name | lowercase }}
                        </div>
                    </div>
                    <div class="col-sm-8 m-t-lg">
                        <h5>Konto</h5>
                        <span class="lead">{{ resourceName }}</span>
                        <div v-if="channelConnected" class="small">
                            {{ channel.reach }} {{ platform.reach_name | lowercase }}
                        </div>
                        <div v-if="hasResourcePicker" class="small">
                            <slot name="resourcePicker" :channel="channel"></slot>
                        </div>
                    </div>
                </div>

                <div class="ChannelCard__button-bar row sm:flex v-bottom">
                    <div class="col-sm-6">
                        <div v-if="!channelConnected || isConfirmedDisconnect">
                            <a
                                :href="urlAuthorize"
                                class="btn btn-load btn-success"
                                :disabled="channelConnecting"
                                data-turbolinks="false"
                            >
                                <i class="fa fa-bolt"></i> Forbind nu
                            </a>
                        </div>
                        <button v-else type="button" class="btn btn-default" @click="disconnect()">
                            Afbryd forbindelse
                        </button>
                    </div>
                    <div v-if="channel" class="col-sm-6 small text-right">
                        Senest opdateret for {{ lastUpdated }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="less" type="text/less">
@import '~dashboard-less/variables';

.ChannelCard {
    .PlatformIcon {
        font-size: 50px;
    }
    .ChannelCard__status .label {
        padding: 6px 12px;
    }

    .ChannelCard__button-bar {
        @media (max-width: @screen-xs-max) {
            .btn {
                width: 100%;
                margin-bottom: 10px;
            }
        }
    }

    /*.ChannelCard__status-alert-list {*/
    /*    margin-top: 5px;*/
    /*    margin-bottom: 2px;*/
    /*}*/

    .fa-bolt {
        color: yellow;
        font-size: 150%;
        position: relative;
        top: 2px;
        left: -5px;
    }
}
</style>

<script type="text/babel">
export default {
    props: ['channels', 'platform'],

    data: () => ({
        isConfirmedDisconnect: false,
    }),

    computed: {
        channel() {
            let match = null

            this.channels.forEach((channel, idx) => {
                if (parseInt(channel.platform_id) === this.platform.id) {
                    match = idx
                }
            })

            return match === null ? null : this.channels[match]
        },

        channelConnecting() {
            return this.channel && this.channel.status === 'connecting'
        },

        channelConnected() {
            return this.channel && ['active', 'unavailable'].indexOf(this.channel.status) > -1
        },

        channelFailed() {
            return this.channel && this.channel.status === 'failed'
        },

        isPersonalAccount() {
            return (
                this.channelFailed &&
                this.channel.failed_reason &&
                this.channel.failed_reason.type &&
                this.channel.failed_reason.type.includes('PersonalAccountException')
            )
        },

        hasResourcePicker() {
            return typeof this.$scopedSlots.resourcePicker !== 'undefined'
        },

        lastUpdated() {
            return moment().to(moment.min(moment(), moment(this.channel.refreshed_at)))
        },

        resourceName() {
            const name = this.$get(this.channel, 'resource_name')

            return name === null ? '-' : name
        },

        urlAuthorize() {
            return `/platforms/${this.platform.slug}/authorization`
        },

        urlUnauthorize() {
            return `/platforms/${this.platform.slug}/authorization/revoke`
        },
    },

    methods: {
        disconnect(confirmed) {
            if (typeof confirmed === 'undefined') {
                swal(
                    {
                        title: `Afbryd forbindelse til "${this.platform.name}"?`,
                        text: 'Du vil ikke længere kunne anmode om job til denne type kampagne',
                        customClass: 'danger',
                        showCancelButton: true,
                        confirmButtonText: 'Afbryd forbindelse',
                        cancelButtonText: 'Annuller',
                        closeOnConfirm: true,
                        allowEscapeKey: true,
                        allowClickOutside: true,
                        type: 'warning',
                    },
                    this.disconnect
                )
            }
            if (confirmed) {
                axios
                    .get(this.urlUnauthorize)
                    .then(() => {
                        this.isConfirmedDisconnect = true
                    })
                    .catch(error => {
                        swal({
                            title: 'Der opstod en fejl',
                            customClass: 'danger',
                            showCancelButton: true,
                            showConfirmButton: false,
                            cancelButtonText: 'Bekræft',
                            closeOnConfirm: false,
                            allowEscapeKey: true,
                            allowClickOutside: true,
                            type: 'warning',
                        })

                        console.error(error)
                    })
            }
        },
    },
}
</script>
